import { getPortalDashboardUrl } from "@bezoned/shared/routes.js";
import { Body2, Tooltip } from "@fluentui/react-components";
import { DismissCircle24Regular } from "@fluentui/react-icons";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";

import { env } from "../../utils/env.js";

const CLOSE_ANIMATION_MS = 300;

export const ActiveTrialBanner = ({
  show,
  expiresAt,
}: {
  show: boolean;
  expiresAt: Date | null;
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    const updateHeight = () => {
      if (!contentRef.current) {
        return;
      }

      setMaxHeight(contentRef.current.scrollHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  const handleDismiss = () => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
    }, CLOSE_ANIMATION_MS);
  };

  if (!(show && expiresAt && isVisible)) {
    return null;
  }

  return (
    <div
      className="bg-bzd-yellow-200 w-full overflow-hidden transition-all ease-in-out"
      style={{
        maxHeight: isClosing ? 0 : (maxHeight ?? undefined),
        opacity: isClosing ? 0 : 1,
        transitionDuration: `${CLOSE_ANIMATION_MS}ms`,
      }}
    >
      <div
        className="mx-auto flex w-full items-center justify-center gap-4 px-4 py-2 text-sm md:w-3/5"
        ref={contentRef}
      >
        <Body2>
          You're currently on a free trial of BeZoned, ending on{" "}
          <strong>
            {DateTime.fromJSDate(expiresAt).toLocaleString(DateTime.DATE_SHORT)}
          </strong>
          . You can manage your BeZoned license{" "}
          <Tooltip
            content="Open subscription dashboard"
            relationship="description"
          >
            <a
              className="underline"
              href={getPortalDashboardUrl(env.PUBLIC_PORTAL_URL)}
              rel="noopener"
              target="_blank"
            >
              in the subscription dashboard.
            </a>
          </Tooltip>
        </Body2>

        <Tooltip content="Dismiss warning" relationship="label">
          <button
            className="shrink-0 rounded px-2 py-1 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={handleDismiss}
            type="button"
          >
            <DismissCircle24Regular />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};
